import { FileText, Users, Shield, Scale, CreditCard, AlertTriangle } from "lucide-react";
import LegalPage from "../Components/LegalPage";

const Section = ({ title, Icon, children }) => (
  <section className="mb-7 scroll-mt-24 last:mb-0 sm:mb-8">
    <h2 className="flex items-start gap-2.5 font-display text-[16px] font-bold leading-6 text-ink-800 sm:text-lg">
      {Icon && (
        <Icon size={17} className="mt-1 shrink-0 text-brand-700" />
      )}
      <span>{title}</span>
    </h2>
    <div className="mt-3 space-y-3.5 border-b border-sand-100 pb-6 text-[13px] leading-6 text-ink-600 sm:mt-4 sm:pb-7 sm:text-[14px] sm:leading-7">
      {children}
    </div>
  </section>
);

const Card = ({ title, children, tone }) => (
  <div className={
    tone === "warning"
      ? "border-l-2 border-rose-300 pl-4"
      : tone === "amber"
        ? "border-l-2 border-amber-300 pl-4"
        : ""
  }>
    {title && (
      <h3 className={`mb-2 text-[13px] font-semibold ${
        tone === "warning" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : "text-ink-800"
      }`}>
        {title}
      </h3>
    )}
    <div className="text-[12.5px] leading-5.5 text-ink-600 sm:text-[13px] sm:leading-6 [&_li]:pl-0.5 [&_strong]:font-semibold [&_strong]:text-ink-800 [&_ul]:marker:text-brand-500">
      {children}
    </div>
  </div>
);

const TermsConditions = () => (
  <LegalPage
    title="Terms & Conditions"
    subtitle="Understanding our terms helps ensure a safe and respectful matchmaking experience for everyone."
    Icon={FileText}
    lastUpdated="June 6, 2026"
  >
    <Section title="1. Acceptance of Terms" Icon={Scale}>
      <Card>
        <p>
          Welcome to Karanki Matrimony. These Terms & Conditions govern your use of our matrimonial platform and services. By registering, accessing or using our platform, you confirm that you have read, understood and agreed to be bound by these Terms.
        </p>
        <p className="mt-2">If you do not agree to these Terms, you must not use our platform or services.</p>
      </Card>
    </Section>

    <Section title="2. About Karanki Matrimony">
      <Card>
        <p>
          Karanki Matrimony is an online matrimonial platform exclusively for the Goud / Gowda community, operated from Hyderabad, Telangana, India. Our platform helps members find life partners through profile listings, compatibility matching and direct communication.
        </p>
      </Card>
    </Section>

    <Section title="3. Eligibility — 18+ Only" Icon={Users}>
      <Card tone="amber" title="Age requirement — strictly 18 years and above">
        <p className="text-[13.5px] text-amber-800">
          You must be <strong>at least 18 years of age</strong> to register and use Karanki Matrimony. By creating an account, you confirm that you meet this age requirement. Profiles found to belong to minors will be immediately removed and the account banned.
        </p>
      </Card>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Eligibility requirements">
          <ul className="list-disc space-y-1 pl-5">
            <li>Must be 18 years or older</li>
            <li>Legally capable of entering into a binding contract</li>
            <li>Genuine intent to find a life partner</li>
            <li>Belonging to or connected with the Goud / Gowda community</li>
          </ul>
        </Card>
        <Card title="Community values">
          <ul className="list-disc space-y-1 pl-5">
            <li>Respect cultural and traditional values</li>
            <li>Honour family and community norms</li>
            <li>Maintain dignity in all interactions</li>
            <li>Follow ethical matchmaking practices</li>
          </ul>
        </Card>
      </div>
    </Section>

    <Section title="4. Account Registration & Authenticity" Icon={Shield}>
      <Card tone="warning" title="Strict authenticity policy">
        <p className="text-[13.5px] text-rose-800">
          All information provided during registration and profile creation must be <strong>accurate, genuine and up to date</strong>. Karanki Matrimony strictly prohibits:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-[13.5px] text-rose-800">
          <li>Creating a fake, duplicate or impersonation account</li>
          <li>Uploading photos of another person without their consent</li>
          <li>Providing false details about age, marital status, education, profession or identity</li>
          <li>Creating an account on behalf of someone else without their knowledge</li>
          <li>Using the platform for purposes other than genuine matrimonial intent</li>
        </ul>
        <p className="mt-3 text-[13.5px] font-semibold text-rose-800">
          Violation of this policy will result in <strong>immediate account suspension or permanent ban</strong> without refund. Legal action may be initiated in serious cases.
        </p>
      </Card>
      <Card title="Account security">
        <ul className="list-disc space-y-1 pl-5">
          <li>One person may not maintain more than one active account</li>
          <li>Keep your login credentials confidential</li>
          <li>Notify us immediately of any unauthorised access</li>
          <li>You are responsible for all activity under your account</li>
        </ul>
      </Card>
    </Section>

    <Section title="5. User Conduct">
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Expected behaviour">
          <ul className="list-disc space-y-1 pl-5">
            <li>Treat other users with respect and dignity</li>
            <li>No harassment, abuse or threatening communication</li>
            <li>No discrimination based on caste, religion or gender</li>
            <li>Do not share another user's contact details publicly</li>
          </ul>
        </Card>
        <Card title="Prohibited actions">
          <ul className="list-disc space-y-1 pl-5">
            <li>No spam, scam or fraudulent activity</li>
            <li>No offensive, harmful or misleading content</li>
            <li>No use of the platform for commercial solicitation</li>
            <li>Report suspicious or abusive profiles immediately</li>
          </ul>
        </Card>
      </div>
    </Section>

    <Section title="6. Profile Verification">
      <Card>
        <p>
          Karanki Matrimony verifies profiles through an admin review process before they become visible to other members. Verification confirms that a profile appears genuine but does not constitute a guarantee of the individual's character, intent or background.
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5">
          <li>We reserve the right to request identity documents at any time</li>
          <li>Profiles that fail verification will not be approved</li>
          <li>Previously verified profiles may be re-reviewed if concerns are raised</li>
          <li>Karanki Matrimony is not liable for the conduct of verified users off-platform</li>
        </ul>
      </Card>
    </Section>

    <Section title="7. Payments & Premium Membership" Icon={CreditCard}>
      <Card title="Premium services">
        <ul className="list-disc space-y-1 pl-5">
          <li>Certain features require a paid Premium membership</li>
          <li>All fees and duration are clearly disclosed before purchase</li>
          <li>Payments are securely processed through Razorpay</li>
          <li>Premium membership is non-transferable</li>
          <li>Prices may be updated with reasonable advance notice</li>
        </ul>
      </Card>
    </Section>

    <Section title="8. Refund Policy" Icon={AlertTriangle}>
      <Card tone="warning" title="No refund policy — please read carefully">
        <p className="text-[13.5px] leading-6 text-rose-800">
          <strong>All payments made on Karanki Matrimony are strictly non-refundable.</strong> Once a Premium membership payment is successfully completed, no refund will be issued under any circumstances, including but not limited to:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-[13.5px] text-rose-800">
          <li>Change of mind after purchase</li>
          <li>Failure to use the premium features during the membership period</li>
          <li>Account suspension due to violation of our Terms & Conditions</li>
          <li>Dissatisfaction with match results</li>
          <li>Accidental or duplicate payments — please contact us immediately for investigation</li>
        </ul>
        <p className="mt-3 text-[13px] text-rose-700">
          In the rare event of a confirmed technical error resulting in a duplicate charge, we will investigate and resolve at our discretion. Please contact us within 7 days of the transaction.
        </p>
      </Card>
    </Section>

    <Section title="9. Account Suspension & Termination">
      <Card>
        <p>
          Karanki Matrimony reserves the right to suspend or permanently terminate any account at any time, with or without notice, for reasons including but not limited to:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5">
          <li>Providing false or misleading information</li>
          <li>Violation of these Terms & Conditions</li>
          <li>Abusive, fraudulent or harmful behaviour</li>
          <li>Receiving multiple valid complaints from other users</li>
          <li>Non-compliance with a verification request</li>
        </ul>
        <p className="mt-3 text-[13.5px]">
          Suspended or banned accounts are not entitled to a refund of any paid membership fees.
        </p>
      </Card>
    </Section>

    <Section title="10. Limitation of Liability">
      <Card>
        <p>
          Karanki Matrimony provides a platform to connect individuals for matrimonial purposes. We do not guarantee the outcome of any match, the accuracy of user-provided information or the conduct of members outside the platform.
        </p>
        <p className="mt-2">
          To the maximum extent permitted by law, Karanki Matrimony is not liable for any indirect, incidental, special or consequential damages arising from your use of our services. Our total liability shall not exceed the amount paid by you for our services in the 12 months preceding a claim.
        </p>
      </Card>
    </Section>

    <Section title="11. Governing Law">
      <Card>
        <p>
          These Terms are governed by the laws of India, specifically the state of Telangana. Any disputes arising from these Terms or the use of our platform shall be subject to the exclusive jurisdiction of the courts of Hyderabad, Telangana.
        </p>
      </Card>
    </Section>

    <Section title="12. Changes to Terms">
      <p>
        We may update these Terms from time to time. You will be notified of significant changes via email or an in-app notice. Continued use of the platform after changes are posted constitutes your acceptance of the revised Terms.
      </p>
    </Section>

    <Section title="13. Contact Us">
      <p>
        If you have questions about these Terms, please contact us via the Contact Us page.
      </p>
    </Section>
  </LegalPage>
);

export default TermsConditions;
